import type { Column, SortDirection } from '../simple-table'
import type { Provider } from './provider'

import { nanoid } from '@reduxjs/toolkit'
import * as Sentry from '@sentry/react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { MdArrowForward } from 'react-icons/md'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { useDebounce } from 'react-use'

import http from '../../../lib/http'
import { useAppDispatch, useAppSelector } from '../../../store'
import { finishLoading, startLoading } from '../../../store/modules'
import { FriendlyDateTime } from '../../shared/Date/friendly-date-time'
import { ErrorToast } from '../../shared/error-toast'
import { Filters } from '../components/filters'
import { useDebouncedFilters } from '../hooks/use-debounced-filters'
import * as Table from '../simple-table'

import styles from './index.module.css'
import { providersSchema } from './schema'
import { Status, statusNameMapper } from './status.enum'

interface ProvidersActionsProps {
  item: Provider
  queryKey: QueryKey
  onAction?: () => Promise<unknown> | unknown
  onError?: (message: string) => Promise<unknown> | unknown
}

type ProviderHistoryAction = 'add' | 'ignore' | 'restore'

function ProvidersActions ({ item, queryKey, onAction, onError }: ProvidersActionsProps): JSX.Element | null {
  let resource = useAppSelector(state => state.globalVars?.params?.id)
  let { status, name } = item

  let dispatch = useAppDispatch()

  interface AddActionMutationPayload {
    action: 'add'
    name: string
    resourceType: string
  }

  interface OtherActionMutationPayload {
    action: Exclude<ProviderHistoryAction, 'add'>
    name: string
  }

  type MutationPayload =
    | AddActionMutationPayload
    | OtherActionMutationPayload

  let queryClient = useQueryClient()

  let mutation = useMutation({
    async mutationFn (payload: MutationPayload) {
      let { action, ...rest } = payload

      return await http.postRequest(`/patients/${resource as string}/claims/providers/${action}`, rest)
    },

    async onMutate (payload) {
      await queryClient.cancelQueries({
        predicate ({ queryKey }) {
          return Array.isArray(queryKey) && queryKey[0] === 'claims-history/providers'
        }
      })

      let previousQueryResponse = queryClient.getQueryData<Response>(queryKey)

      queryClient.setQueryData<Response>(queryKey, old => {
        if (!old) {
          return old
        }

        return {
          ...old,
          data: old.data.map(item => {
            if (item.name === payload.name) {
              return {
                ...item,
                status: payload.action === 'add'
                  ? Status.Current
                  : payload.action === 'ignore'
                    ? Status.Ignored
                    : payload.action === 'restore'
                      ? Status.New
                      : item.status
              }
            }

            return item
          })
        }
      })

      return {
        action: payload.action,
        previousQueryResponse
      }
    },

    onError (err, payload, context) {
      void err

      onError?.('Something went wrong.')
      queryClient.setQueryData(queryKey, context?.previousQueryResponse)
    },

    async onSettled () {
      onAction?.()
    }
  })

  useEffect(() => {
    if (mutation.isPending) {
      let id = nanoid()
      dispatch(startLoading({
        id,
        description: 'save provider'
      }))

      return () => {
        dispatch(finishLoading(id))
      }
    }
  }, [
    dispatch,
    mutation.isPending
  ])

  if (!resource || !name) {
    return null
  }

  return (
    <Table.ActionButtonWrapper>
      {status === 'new' && (
        <>
          <Table.ActionButtonDropdown
            buttonContent={(
              <>
                <button
                  type='button'
                  className={styles.suggestionButton}
                  onClick={() => {
                    mutation.mutate({
                      action: 'add',
                      name: name as string,
                      resourceType: 'Practitioner'
                    })
                  }}
                >
                  <MdArrowForward
                    className={styles.suggestionArrow}
                    style={{ visibility: item.likely_resource_type === 'Practitioner' ? 'visible' : 'hidden' }}
                  />

                  <span>
                    Add to Medical Team
                  </span>
                </button>

                <button
                  type='button'
                  className={styles.suggestionButton}
                  onClick={() => {
                    mutation.mutate({
                      action: 'add',
                      name: name as string,
                      resourceType: 'Organization'
                    })
                  }}
                >
                  <MdArrowForward
                    className={styles.suggestionArrow}
                    style={{ visibility: item.likely_resource_type === 'Organization' ? 'visible' : 'hidden' }}
                  />

                  <span>
                    Add to Organization
                  </span>
                </button>
              </>
            )}
          >
            <Table.ActionButton
              primary
              isLoading={mutation.isPending && mutation.context?.action === 'add'}
              isDisabled={mutation.isPending}
            >
              Add
            </Table.ActionButton>
          </Table.ActionButtonDropdown>

          <Table.ActionButton
            isLoading={mutation.isPending && mutation.context?.action === 'ignore'}
            isDisabled={mutation.isPending}
            onClick={() => {
              mutation.mutate({
                action: 'ignore',
                name: name as string
              })
            }}
          >
            Ignore
          </Table.ActionButton>
        </>
      )}

      {status === 'ignored' && (
        <Table.ActionButton
          isLoading={mutation.isPending && mutation.context?.action === 'restore'}
          isDisabled={mutation.isPending}
          onClick={() => {
            mutation.mutate({
              action: 'restore',
              name: name as string
            })
          }}
        >
          Restore
        </Table.ActionButton>
      )}
    </Table.ActionButtonWrapper>
  )
}

type QueryKey = [
  string,
  string,
  SearchParams
]

interface Response {
  data: Provider[]
  count: number
  total: number
  filters: Record<string, Array<string | number>>
  updated_at: string
}

interface SearchParams extends Record<string, unknown>{
  query: string

  filters: {
    status: Status[]
  }

  sort: {
    field: keyof Provider
    direction: SortDirection
  }

  pagination: {
    page: number
    pageSize: number
  }
}

let defaultSearchParams: SearchParams = {
  query: '',

  filters: {
    status: [
      Status.New
    ]
  },

  sort: {
    field: 'claims',
    direction: 'desc'
  },

  pagination: {
    page: 0,
    pageSize: 10
  }
}

function isDefaultSearchParams (params: SearchParams): boolean {
  return JSON.stringify(params) === JSON.stringify(defaultSearchParams)
}

interface SavedSearchParams {
  searchParams: SearchParams
  timestamp: number
}

interface ProvidersClaimsTableProps {
  onAction?: () => void
  onMount?: (refetch: () => Promise<unknown> | unknown) => void
}

export function ProvidersClaimsTable ({ onAction, onMount }: ProvidersClaimsTableProps): JSX.Element {
  let [
    debouncedSearchParams,
    searchParams,
    setSearchParams,
    setDebouncedSearchParams
  ] = useDebouncedFilters<SearchParams>(defaultSearchParams)

  let [ searchQuery, setSearchQuery ] = useState('')
  let [ error, setError ] = useState<string | undefined>()

  let client = useQueryClient()
  let dispatch = useAppDispatch()

  useDebounce(() => {
    setDebouncedSearchParams(params => ({
      ...params,
      query: searchQuery,
      pagination: {
        ...params.pagination,
        page: 0
      }
    }))
  }, 500, [ searchQuery ])

  // TODO: this should probably be removed when Compassion provider grids are
  // refactored. Currently they reload the whole page on every action, so the
  // filters state is lost. This is a temporary workaround to preserve the
  // search params state.
  useEffect(() => {
    let input = window.localStorage.getItem('claims-history/providers/filters')
    let timestamp = Date.now()

    let parsed: SavedSearchParams | undefined

    try {
      parsed = JSON.parse(input ?? '')
    } catch (err) {}

    if (!parsed) {
      return
    }

    if (parsed.timestamp < timestamp - 1000 * 60) {
      return
    }

    setSearchParams(parsed.searchParams)
  }, [ setSearchParams ])

  useEffect(() => {
    let value: SavedSearchParams = {
      searchParams,
      timestamp: Date.now()
    }

    window.localStorage.setItem('claims-history/providers/filters', JSON.stringify(value))
  }, [ searchParams ])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let resource = useSelector<any, string>(state => state.globalVars?.params?.id)

  let queryKey: QueryKey = useMemo(() => [
    'claims-history/providers',
    resource,
    debouncedSearchParams
  ], [ resource, debouncedSearchParams ])

  let query = useQuery<Response, unknown, Response, QueryKey>({
    queryKey,
    async queryFn ({ queryKey }): Promise<Response> {
      let [ , resource, searchParams ] = queryKey
      let { query, sort, filters, pagination } = searchParams
      let normalizedQuery = query.trim().toLowerCase()

      let urlSearchParams = new URLSearchParams([
        [ 'on', sort.field ],
        [ 'dir', sort.direction ],
        [ 'page', pagination.page.toString(10) ],
        [ 'size', pagination.pageSize.toString(10) ],
        ...filters.status.map(status => [ 'status', status ])
      ])

      if (normalizedQuery.length > 0) {
        urlSearchParams.set('search', normalizedQuery)
      }

      let response: Response = await http.getRequest(`/patients/${resource}/claims/providers?${urlSearchParams.toString()}`)
      let data = providersSchema.parse(response.data)
      return {
        ...response,
        data
      }
    },
    enabled: typeof resource === 'string',
    retry: false,
    staleTime: 1000 * 60 * 5,

    placeholderData: keepPreviousData,
    refetchOnReconnect: true,
    refetchOnWindowFocus: false
  })

  let handlePageChange = useCallback((value: number): void => {
    setDebouncedSearchParams(params => ({
      ...params,
      pagination: {
        ...params.pagination,
        page: value - 1
      }
    }))
  }, [ setDebouncedSearchParams ])

  let handlePageSizeChange = useCallback((value: number): void => {
    setDebouncedSearchParams(params => ({
      ...params,
      pagination: {
        ...params.pagination,
        page: 0,
        pageSize: value
      }
    }))
  }, [ setDebouncedSearchParams ])

  let handleSortChange = useCallback((field: keyof Provider, direction: SortDirection): void => {
    setSearchParams(params => ({
      ...params,
      pagination: {
        ...params.pagination,
        page: 0
      },
      sort: {
        ...params.sort,
        field,
        direction
      }
    }))
  }, [ setSearchParams ])

  let handleFiltersClear = useCallback((): void => {
    setSearchParams(params => ({
      ...params,
      query: '',
      pagination: {
        ...params.pagination,
        page: 0
      },
      filters: {
        ...params.filters,
        status: []
      }
    }))
    setSearchQuery('')
  }, [ setSearchParams ])

  useEffect(() => {
    if (query.status === 'pending' || query.fetchStatus === 'fetching') {
      let id = nanoid()

      dispatch(startLoading({
        id,
        description: 'fetch provider claims'
      }))

      return () => {
        dispatch(finishLoading(id))
      }
    }
  }, [
    query.status,
    query.fetchStatus,
    dispatch
  ])

  let filters = useMemo(() => {
    let keys: Array<keyof SearchParams['filters']> = [
      'status'
    ]

    return keys.map(key => {
      let options = (query.data?.filters[key] ?? [])
        .map((value: string | number) => {
          let label = value

          if (key === 'status') {
            label = statusNameMapper[value as Status] ?? value
          }

          return {
            label: label.toString(),
            value
          }
        })

      return {
        name: key,
        value: searchParams.filters[key],
        options
      }
    })
  }, [ query.data?.filters, searchParams.filters ])

  let handleAction = useCallback(async () => {
    await client.invalidateQueries({
      predicate ({ queryKey }) {
        return Array.isArray(queryKey) && queryKey[0] === 'claims-history/providers'
      }
    })

    await query.refetch()
    onAction?.()
  }, [
    query,
    onAction,
    client
  ])

  let handleExternalRefetch = useCallback(async () => {
    await client.invalidateQueries({
      predicate ({ queryKey }) {
        return Array.isArray(queryKey) && queryKey[0] === 'claims-history/providers'
      }
    })

    await query.refetch()
  }, [
    client,
    query
  ])

  useEffect(() => {
    onMount?.(handleExternalRefetch)
  }, [ onMount, handleExternalRefetch ])

  let handleError = useCallback((message: string): void => {
    setError(message)
    setTimeout(() => setError(undefined), 10000)
  }, [])

  let handleFilterChange = useCallback((name: string, value: Array<string | number | boolean>): void => {
    setSearchParams(params => ({
      ...params,
      pagination: {
        ...params.pagination,
        page: 0
      },
      filters: {
        ...params.filters,
        [name]: value
      }
    }))
  }, [ setSearchParams ])

  let columns = useMemo<Array<Column<Provider>>>(() => [
    {
      name: 'name',
      header: 'Provider Name',
      isMergeable: true,
      isSortable: true,
      gridArea: '1.5fr',
      getValue ({ name }) {
        if (typeof name !== 'string') {
          return null
        }

        return (
          <Link
            to={`../claims-history?provider_name=${encodeURIComponent(name)}&force=provider_name`}
            className={styles.providerLink}
          >
            <strong>{name}</strong>
          </Link>
        )
      }
    },
    {
      name: 'provider_key',
      header: 'Provider Key',
      isMergeable: true,
      isSortable: true,
      gridArea: '0.75fr',
      style: {
        textAlign: 'center'
      },
      headerStyle: {
        display: 'flex',
        justifyContent: 'center'
      }
    },
    {
      name: 'claims',
      header: (
        <div
          title='Number of Claims'
          style={{
            fontSize: 'inherit',
            fontWeight: 'inherit'
          }}
        >
          Claims
        </div>
      ),
      initialSortDirection: 'desc',
      isSortable: true,
      gridArea: '0.75fr',
      style: {
        textAlign: 'right'
      },
      headerStyle: {
        display: 'flex',
        justifyContent: 'flex-end'
      }
    },
    {
      name: 'first_claim_date',
      header: (
        <div
          title='First Claim Date'
          style={{
            fontSize: 'inherit',
            fontWeight: 'inherit'
          }}
        >
          First
        </div>
      ),
      isSortable: true,
      initialSortDirection: 'desc',
      getValue (item) {
        if (typeof item.first_claim_date !== 'string') {
          return null
        }
        return Table.formatDate(item.first_claim_date)
      },
      gridArea: '0.75fr',
      style: {
        textAlign: 'right'
      },
      headerStyle: {
        display: 'flex',
        justifyContent: 'flex-end'
      }
    },
    {
      name: 'last_claim_date',
      header: (
        <div
          title='Last Claim Date'
          style={{
            fontSize: 'inherit',
            fontWeight: 'inherit'
          }}
        >
          Last
        </div>
      ),
      isSortable: true,
      initialSortDirection: 'desc',
      getValue (item) {
        if (typeof item.last_claim_date !== 'string') {
          return null
        }
        return Table.formatDate(item.last_claim_date)
      },
      gridArea: '0.75fr',
      style: {
        textAlign: 'right'
      },
      headerStyle: {
        display: 'flex',
        justifyContent: 'flex-end'
      }
    },
    {
      name: 'places_of_service',
      header: 'Places of Service',
      isSortable: true,
      getValue (item) {
        if (!Array.isArray(item.places_of_service)) {
          return null
        }

        return item.places_of_service
          ?.filter(Boolean)
          .join(', ')
      },
      gridArea: '1.5fr'
    },
    {
      name: 'status',
      header: 'Status',
      isSortable: true,
      initialSortDirection: 'asc',
      getValue (item) {
        return typeof item.status === 'string'
          ? statusNameMapper[item.status] ?? '–'
          : '–'
      }
    },
    {
      name: 'actions',
      header: 'Actions',
      getValue (item) {
        return (
          <ProvidersActions
            item={item}
            queryKey={queryKey}
            onAction={handleAction}
            onError={handleError}
          />
        )
      }
    }
  ], [ queryKey, handleAction, handleError ])

  return (
    <>
      <Table.Table
        header={(
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h2>Claims History | Providers</h2>
            {query.status === 'success' && (
              <div>
                <Filters
                  search={searchQuery}
                  onSearchChange={setSearchQuery}
                />
              </div>
            )}
          </div>
        )}
        columns={columns}
        data={query.data?.data ?? []}
        filters={filters}
        sort={searchParams.sort}
        pagination={{
          ...searchParams.pagination,
          totalFiltered: query.data?.total,
          totalUnfiltered: query.data?.count,
          extra: query.data?.updated_at
            ? (
              <span>
                Claims information refreshed
                {' '}
                <FriendlyDateTime
                  datetime={query.data.updated_at}
                  diff
                />
              </span>
            )
            : undefined
        }}
        error={query.error as Error}
        queryStatus={query.status}
        fetchStatus={query.fetchStatus}
        loadingIndicator={false}
        onSortChange={handleSortChange}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onFilterChange={handleFilterChange}
        onFiltersClear={handleFiltersClear}
        onRefetch={() => {
          if (isDefaultSearchParams(searchParams)) {
            return query.refetch()
          }

          client.invalidateQueries({
            queryKey: [
              'claims-history/providers',
              resource
            ]
          })
            .then(() => {
              setSearchParams(defaultSearchParams)
            })
            .catch(err => {
              console.error(err)
              Sentry.captureException(err)
            })
        }}
      />

      <ErrorToast message={error} />
    </>
  )
}
